/* BM-SF03.01 — Bridge owns customer filtering/pagination; local preview is editor-only. */
(() => {
  const initialized = new WeakSet();
  const setBudgetFormat = new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' });
  const formatSetBudget = value => setBudgetFormat.format(Number(value));

  function initializeFixture(root) {
    if (initialized.has(root)) return;
    initialized.add(root);
    const form = root.querySelector('[data-bm-new-form]');
    if (!form) return;
    // Never submit preview controls into a real collection query.
    form.addEventListener('submit', (event) => event.preventDefault());
    const isDesignFixture = root.hasAttribute('data-design-mode-fixture');
    if (!isDesignFixture) return;

    const region = root.querySelector('[data-bm-new-results-region]');
    const grid = root.querySelector('[data-bm-new-products]');
    const rows = [...grid.querySelectorAll('[data-bm-new-fixture-card]')];
    const count = root.querySelector('[data-bm-new-count]');
    const empty = root.querySelector('[data-bm-new-empty]');
    const pagination = root.querySelector('[data-bm-new-pagination]');
    const filters = root.querySelector('.bm-new-results__filters');
    const toggle = filters.querySelector('summary');
    const openButton = root.querySelector('[data-bm-new-filter-open]');
    const openButtonLabel = openButton?.querySelector('span');
    const activeFilters = root.querySelector('[data-bm-new-active-filters]');
    const emptyClear = root.querySelector('[data-bm-new-empty-clear]');
    const desktop = window.matchMedia('(min-width: 781px)');
    const pageSize = root.dataset.pageSize === '2' ? 2 : 4;
    let page = 1;
    let frame = null;
    let revision = 0;

    const syncPanel = () => { filters.open = desktop.matches; };
    syncPanel();
    desktop.addEventListener('change', syncPanel);
    root.querySelector('[data-bm-new-filter-fields]').disabled = false;
    form.elements.sort_by.disabled = false;

    function values(name) {
      return [...form.querySelectorAll(`input[name="${name}"]:checked`)].map((input) => input.value);
    }

    function priceBound(name) {
      const input = form.elements[name];
      if (input.value === '' || !input.validity.valid) return null;
      const value = Number(input.value);
      return Number.isFinite(value) ? Math.round(value * 100) : null;
    }

    function selectedFilters() {
      const selected = [];
      form.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
        const label = input.closest('label')?.querySelector('span')?.textContent?.trim() || input.value;
        selected.push({ kind: 'checkbox', name: input.name, value: input.value, label });
      });
      for (const name of ['price_min', 'price_max']) {
        const input = form.elements[name];
        if (!input || input.value === '' || !input.validity.valid) continue;
        const prefix = name === 'price_min' ? 'Vanaf' : 'Tot';
        selected.push({ kind: 'price', name, value: input.value, label: `${prefix} ${formatSetBudget(input.value)} per set (4 velgen)` });
      }
      return selected;
    }

    function renderActiveFilters() {
      if (!activeFilters) return;
      const selected = selectedFilters();
      activeFilters.replaceChildren();
      selected.forEach((filter) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'bm-new-results__filter-chip';
        button.dataset.filterKind = filter.kind;
        button.dataset.filterName = filter.name;
        button.dataset.filterValue = filter.value;
        button.setAttribute('aria-label', `Verwijder filter ${filter.label}`);
        const label = document.createElement('span');
        label.textContent = filter.label;
        const close = document.createElement('span');
        close.setAttribute('aria-hidden', 'true');
        close.className = 'bm-new-results__filter-chip-close';
        button.append(label, close);
        activeFilters.append(button);
      });
      if (selected.length > 1) {
        const clear = document.createElement('button');
        clear.type = 'button';
        clear.className = 'bm-new-results__filter-clear-inline';
        clear.dataset.filterClearAll = '';
        clear.textContent = 'Wis alles';
        activeFilters.append(clear);
      }
      activeFilters.hidden = selected.length === 0;
      if (openButtonLabel) openButtonLabel.textContent = selected.length ? `Filters (${selected.length})` : 'Filters';
    }

    function matchingRows() {
      const facets = Object.fromEntries(['diameter', 'finish', 'color', 'manufacturer', 'design'].map((name) => [name, values(name)]));
      const minimum = priceBound('price_min');
      const maximum = priceBound('price_max');
      const matches = rows.filter((row) => {
        const data = row.dataset;
        const price = Number(data.setPrice);
        return (!facets.diameter.length || facets.diameter.some((diameter) => data.diameters.split(',').includes(diameter)))
          && (!facets.finish.length || facets.finish.some((finish) => String(data.finishes || '').split('|').includes(finish)))
          && ['color', 'manufacturer', 'design'].every((name) => !facets[name].length || facets[name].includes(data[name]))
          && (minimum === null || price >= minimum)
          && (maximum === null || price <= maximum);
      });
      // Sort the explicitly supplied fixture set totals, never calculate offers.
      const identityOrder = (a, b) => `${a.dataset.manufacturer} ${a.dataset.design}`.localeCompare(`${b.dataset.manufacturer} ${b.dataset.design}`, 'nl');
      const sort = form.elements.sort_by.value;
      return matches.sort((a, b) => {
        if (sort === 'price-ascending') return Number(a.dataset.setPrice) - Number(b.dataset.setPrice) || identityOrder(a, b);
        if (sort === 'price-descending') return Number(b.dataset.setPrice) - Number(a.dataset.setPrice) || identityOrder(a, b);
        return identityOrder(a, b);
      });
    }

    function pageLink(number, label, ariaLabel) {
      const link = document.createElement('a');
      link.href = `#${root.id}`;
      link.textContent = label;
      link.dataset.page = String(number);
      link.setAttribute('aria-label', ariaLabel);
      return link;
    }

    function render() {
      renderActiveFilters();
      const matches = matchingRows();
      const pages = Math.ceil(matches.length / pageSize);
      page = Math.max(1, Math.min(page, pages || 1));
      rows.forEach((row) => { row.hidden = true; });
      const visible = matches.slice((page - 1) * pageSize, page * pageSize);
      visible.forEach((row) => { row.hidden = false; grid.append(row); });
      count.textContent = `${matches.length} ${matches.length === 1 ? 'velgdesign' : 'velgdesigns'}`;
      empty.hidden = matches.length !== 0;
      grid.hidden = matches.length === 0;
      pagination.replaceChildren();
      pagination.hidden = pages <= 1;
      if (page > 1) pagination.append(pageLink(page - 1, '←', 'Vorige pagina'));
      for (let number = 1; number <= pages; number += 1) {
        if (number === page) {
          const current = document.createElement('span');
          current.className = 'is-current';
          current.textContent = String(number);
          current.setAttribute('aria-current', 'page');
          current.tabIndex = -1;
          pagination.append(current);
        } else pagination.append(pageLink(number, String(number), `Pagina ${number}`));
      }
      if (page < pages) pagination.append(pageLink(page + 1, '→', 'Volgende pagina'));
      root.dataset.state = matches.length ? 'ready' : 'empty';
      region.setAttribute('aria-busy', 'false');
    }

    function schedule(resetPage = true, focusPage = false) {
      if (resetPage) page = 1;
      const expectedRevision = ++revision;
      if (frame !== null) cancelAnimationFrame(frame);
      root.dataset.state = 'loading';
      region.setAttribute('aria-busy', 'true');
      // Keep existing rows in place while pending, avoiding a blank grid flash.
      frame = requestAnimationFrame(() => {
        frame = null;
        if (expectedRevision !== revision || !root.isConnected) return;
        render();
        if (focusPage) pagination.querySelector('[aria-current="page"]')?.focus();
      });
    }

    openButton?.addEventListener('click', () => {
      filters.open = true;
      filters.querySelector('input, button, select')?.focus();
    });
    activeFilters?.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button || !activeFilters.contains(button)) return;
      if (button.hasAttribute('data-filter-clear-all')) {
        form.reset();
        return;
      }
      const name = button.dataset.filterName;
      const value = button.dataset.filterValue;
      if (!name) return;
      if (button.dataset.filterKind === 'price') {
        const input = form.elements[name];
        if (input) input.value = '';
      } else {
        form.querySelectorAll(`input[name="${name}"]`).forEach((input) => {
          if (input.value === value) input.checked = false;
        });
      }
      schedule();
    });
    emptyClear?.addEventListener('click', () => form.reset());
    form.addEventListener('change', () => schedule());
    form.addEventListener('submit', () => {
      schedule();
      if (!desktop.matches) { filters.open = false; (openButton || toggle).focus(); }
    });
    form.addEventListener('reset', () => { queueMicrotask(() => schedule()); });
    filters.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !desktop.matches) { filters.open = false; (openButton || toggle).focus(); }
    });
    pagination.addEventListener('click', (event) => {
      const link = event.target.closest('[data-page]');
      if (!link || !pagination.contains(link)) return;
      event.preventDefault();
      page = Number(link.dataset.page);
      schedule(false, true);
    });
    // Theme Editor fixtures never navigate to fictitious products.
    // Real visual-certification cards use their actual public Shopify URLs.
    if (isDesignFixture) {
      grid.addEventListener('click', (event) => {
        if (event.target.closest('a')) event.preventDefault();
      });
    }
    root.addEventListener('bm-new-results:dispose', () => {
      ++revision;
      if (frame !== null) cancelAnimationFrame(frame);
      desktop.removeEventListener('change', syncPanel);
    }, { once: true });
    render();
  }

  const displayLabel = value => window.BandenmeestersDisplayLabels?.format(value) ?? String(value ?? '');
  const catalogFacetNames = ['diameter', 'finish', 'manufacturer', 'design'];
  const catalogSortValues = ['brand-design', 'price-ascending', 'price-descending'];

  function resultSupplierStatus(finish, vehicleId, client) {
    if (!vehicleId) return null;
    const sets = finish?.setOptions || [];
    const matched = sets.length > 0 && sets.every(option => client?.hasSupplierMatch?.(option));
    return { state: matched ? 'matched' : 'not-confirmed',
      label: matched ? 'Geschikt voor jouw auto' : 'Passing nog niet bevestigd' };
  }

  // Result navigation is an explicit selection. Page rendering never replaces the
  // garage or starts a provider search. The PDP still reads authoritative passing.
  function bindResultProductLink(link, product, vehicle) {
    const target = new URL(product.productUrl, window.location.href);
    const options = product.setOptions || [];
    const client = window.BandenmeestersNewClient;
    const confirmed = vehicle?.newVehicle?.vehicleId
      ? options.filter(option => option.setType === 'SQUARE' && option.defaultQuantity === 4
        && client?.hasSupplierMatch?.(option) && option.setId && option.shopifyVariantBinding?.shopifyVariantId)
      : [];
    const eligible = confirmed.length ? confirmed : (!vehicle?.newVehicle?.vehicleId && options.length === 1 ? options : []);
    const preferred = [...eligible].sort((a, b) =>
      (a.unitPrice?.amountCents ?? Number.MAX_SAFE_INTEGER)
      - (b.unitPrice?.amountCents ?? Number.MAX_SAFE_INTEGER))[0];
    if (preferred?.setId && preferred.shopifyVariantBinding?.shopifyVariantId) {
      target.searchParams.set('bm_set', preferred.setId);
      target.searchParams.set('variant', String(preferred.shopifyVariantBinding.shopifyVariantId));
    }
    const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (/^\d+$/.test(preview || '')) target.searchParams.set('preview_theme_id', preview);
    link.href = target.href;
    if (!vehicle?.newVehicle?.vehicleId) return;
    const select = event => {
      const owner = window.BandenmeestersVehicle;
      if (owner?.getNewVehicle?.()?.vehicleId === vehicle.newVehicle.vehicleId) {
        if (vehicle.newVehicle.image) owner.refreshNewVehicle?.({ vehicleId: vehicle.newVehicle.vehicleId, image: vehicle.newVehicle.image });
      } else if (!owner?.setActive?.(vehicle)) {
        event.preventDefault();
        link.closest('[data-bm-new-results]')?.querySelector('[data-bm-new-error]')?.removeAttribute('hidden');
        return;
      }
      // A genuine Bridge vehicle result already includes exact P4 set matches.
      // Pass that read once to the destination PDP; Quote still revalidates
      // price, stock, fitment and supplier evidence before cart/checkout.
      if (/^[1-9]\d*$/.test(product.shopifyProductRef || '') && confirmed.length && confirmed.length <= 60) {
        try {
          window.sessionStorage?.setItem('bm_recent_p4_product_visual_v1', JSON.stringify({
            productRef: product.shopifyProductRef,
            vehicleId: vehicle.newVehicle.vehicleId, setIds: confirmed.map(row => row.setId),
            setMatches: confirmed, viaResult: true, savedAt: Date.now(),
          }));
        } catch (_) {}
      }
    };
    // Covers normal/keyboard activation and opening the result in a new tab.
    for (const type of ['click', 'auxclick', 'contextmenu']) link.addEventListener(type, select);
  }

  function nativeDiscoveryVisible({ scoped, native, families = 0, filtered = false, binding = true, vehicle = false }) {
    return Boolean(scoped && native && binding && !families && !filtered && !vehicle);
  }

  function readCatalogState(href, origin) {
    const params = new URL(href || origin, origin).searchParams;
    const facets = Object.fromEntries(catalogFacetNames.map(name => [name, params.getAll(name).filter(value => value.trim() && value.length <= 120)]));
    const price = name => {
      const value = params.get(name);
      return value !== null && value.trim() && Number.isFinite(Number(value)) && Number(value) >= 0 ? value : '';
    };
    const offset = Number(params.get('offset'));
    return { facets, sort: catalogSortValues.includes(params.get('sort_by')) ? params.get('sort_by') : 'brand-design',
      price_min: price('price_min'), price_max: price('price_max'),
      offset: Number.isInteger(offset) && offset >= 0 && offset % 24 === 0 ? offset : 0,
      browseAll: params.get('browse') === 'all' };
  }

  function catalogStateUrl(href, origin, state) {
    const url = new URL(href || origin, origin);
    [...catalogFacetNames, 'sort_by', 'price_min', 'price_max', 'offset', 'browse'].forEach(name => url.searchParams.delete(name));
    catalogFacetNames.forEach(name => state.facets[name].forEach(value => url.searchParams.append(name, value)));
    if (state.sort !== 'brand-design') url.searchParams.set('sort_by', state.sort);
    ['price_min', 'price_max'].forEach(name => { if (state[name] !== '') url.searchParams.set(name, state[name]); });
    if (state.offset) url.searchParams.set('offset', String(state.offset));
    if (state.browseAll) url.searchParams.set('browse', 'all');
    return url;
  }

  function initializeLive(root) {
    if (initialized.has(root)) return;
    initialized.add(root);
    const form = root.querySelector('[data-bm-new-form]');
    const grid = root.querySelector('[data-bm-new-products]');
    const region = root.querySelector('[data-bm-new-results-region]');
    const count = root.querySelector('[data-bm-new-count]');
    const empty = root.querySelector('[data-bm-new-empty]');
    const error = root.querySelector('[data-bm-new-error]');
    const loading = root.querySelector('[data-bm-new-loading]');
    const pagination = root.querySelector('[data-bm-new-pagination]');
    const template = root.querySelector('[data-bm-new-card-template]');
    const officialMakerLogos = (() => {
      try {
        const entries = JSON.parse(root.querySelector('[data-bm-new-maker-registry]')?.textContent || '[]');
        return new Map(Array.isArray(entries)
          ? entries.filter(entry => entry && typeof entry.name === 'string' && typeof entry.logo === 'string' && entry.logo !== '')
            .map(entry => [entry.name, entry]) : []);
      } catch (_) { return new Map(); }
    })();
    const finishTemplate = root.querySelector('[data-bm-new-finish-template]');
    const panel = root.querySelector('.bm-new-results__filters');
    const openButton = root.querySelector('[data-bm-new-filter-open]');
    const chips = root.querySelector('[data-bm-new-active-filters]');
    const browseNote = root.querySelector('[data-bm-new-browse-note]');
    const browseNoteCopy = root.querySelector('[data-bm-new-browse-note-copy]');
    const returnVehicle = root.querySelector('[data-bm-new-return-vehicle]');
    const searchVehicleButton = empty.querySelector('[data-bm-new-search-vehicle]');
    const heroVehicleAction = root.querySelector('[data-bm-new-hero-vehicle]');
    const catalogBrowseAction = root.querySelector('.bm-new-results__head [data-bm-new-browse-all]');
    const heading = root.querySelector('[data-bm-new-heading]');
    const vehicleMeta = root.querySelector('[data-bm-new-vehicle-meta]');
    const vehicleLogo = root.querySelector('[data-bm-new-vehicle-logo]');
    const filterSheet = panel?.querySelector('.bm-new-results__filter-panel');
    const filterClose = panel?.querySelector('[data-bm-new-filter-close]');
    const filterBackdrop = root.querySelector('[data-bm-new-filter-backdrop]');
    const desktop = window.matchMedia('(min-width: 781px)');
    if (!form || !grid || !template || !finishTemplate) return;
    let revision = 0, abort = null, timer = null, offset = 0, browseAll = false, pendingFacets = null, collectionScope = null;
    let transientRetryRemaining = 1;
    let autoSearchVehicleId = '';
    const scopedCollection = root.hasAttribute('data-bm-new-collection-scope');
    let brandEntryPending = window.location?.hash === '#velgenmerken' && !scopedCollection;
    const nativeDiscovery = root.querySelector('[data-bm-new-native-discovery]');
    const nativePanel = nativeDiscovery?.dataset?.bmNewNativeDiscovery === '' ? nativeDiscovery.querySelector('.bm-new-results__filters') : null;
    if (nativePanel) {
      const closeNativePanel = () => { nativePanel.open = false; nativePanel.querySelector('summary')?.focus(); };
      nativePanel.querySelector('[data-bm-native-filter-close]')?.addEventListener('click', closeNativePanel);
      nativePanel.addEventListener('toggle', () => {
        document.body.classList.toggle('bm-new-filter-open', nativePanel.open && !desktop.matches);
        if (nativePanel.open && !desktop.matches) nativePanel.querySelector('[data-bm-native-filter-close]')?.focus({ preventScroll: true });
      });
      nativePanel.addEventListener('keydown', event => {
        if (desktop.matches || !nativePanel.open) return;
        if (event.key === 'Escape') { event.preventDefault(); closeNativePanel(); }
        if (event.key !== 'Tab') return;
        const controls = [...nativePanel.querySelectorAll('.bm-new-results__filter-panel button,.bm-new-results__filter-panel input,.bm-new-results__filter-panel summary,.bm-new-results__filter-panel a[href]')].filter(node => !node.disabled && node.offsetParent !== null);
        const first = controls[0], last = controls.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      });
      const syncNativePanel = () => { nativePanel.open = desktop.matches; };
      syncNativePanel(); desktop.addEventListener('change', syncNativePanel);
      nativeDiscovery.querySelectorAll('[data-bm-native-facet]').forEach(facet => {
        const search = facet.querySelector('[data-bm-native-facet-search]');
        if (!search) return;
        const choices = [...facet.querySelectorAll('.bm-stock-check')];
        const emptyChoices = facet.querySelector('[data-bm-native-facet-empty]');
        search.addEventListener('keydown', event => { if (event.key === 'Enter') event.preventDefault(); });
        search.addEventListener('input', () => {
          const query = search.value.trim().toLocaleLowerCase('nl-BE');
          let visible = 0;
          choices.forEach(label => { label.hidden = !label.querySelector('input').checked && !label.textContent.toLocaleLowerCase('nl-BE').includes(query); if (!label.hidden) visible++; });
          if (emptyChoices) emptyChoices.hidden = visible > 0;
        });
      });
      const sort = nativeDiscovery.querySelector('select[name="sort_by"]');
      const apply = nativeDiscovery.querySelector('[data-bm-new-native-apply]');
      nativeDiscovery.querySelector('form')?.addEventListener('formdata', event => {
        for (const [name, value] of [...event.formData]) if (value === '') event.formData.delete(name);
      });
      nativeDiscovery.querySelector('form')?.addEventListener('change', event => {
        if (event.target.type === 'checkbox' && event.target.name?.startsWith('filter.')) event.currentTarget.requestSubmit();
      });
      if (sort && apply) {
        sort.addEventListener('change', () => sort.form.requestSubmit());
        apply.hidden = true;
      }
    }
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let filterVersion = 0, filterAnimations = [];
    const cancelFilterMotion = () => { filterAnimations.forEach(animation => animation.cancel()); filterAnimations = []; };
    const syncPanel = () => { closeFilters(false); if (panel) panel.open = desktop.matches; };
    syncPanel(); desktop.addEventListener('change', syncPanel);
    reducedMotion.addEventListener?.('change', () => {
      if (!reducedMotion.matches) return;
      if (openButton?.getAttribute('aria-expanded') === 'false') closeFilters(false);
      else { cancelFilterMotion(); filterSheet?.removeAttribute('inert'); }
    });
    const values = name => [...form.querySelectorAll(`input[name="${name}"]:checked`)].map(input => input.value);
    const selectedValues = name => pendingFacets?.[name] || values(name);
    const money = cents => new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(cents / 100);

    function restoreLocation() {
      const state = readCatalogState(window.location?.href || location.href || location.origin, location.origin);
      pendingFacets = state.facets;
      if (scopedCollection) pendingFacets.manufacturer = [];
      form.elements.sort_by.value = state.sort;
      form.elements.price_min.value = state.price_min;
      form.elements.price_max.value = state.price_max;
      form.querySelectorAll('input[type="checkbox"]').forEach(input => { input.checked = state.facets[input.name]?.includes(input.value) || false; });
      offset = state.offset;
      browseAll = !Boolean(root.dataset.exactVehicleId) && state.browseAll;
    }

    function writeLocation(mode) {
      if (mode === 'none' || !window.history || !window.location?.href) return;
      const state = { facets: Object.fromEntries(catalogFacetNames.map(name => [name, selectedValues(name)])),
        sort: form.elements.sort_by.value, price_min: form.elements.price_min.value,
        price_max: form.elements.price_max.value, offset,
        browseAll: Boolean(root.dataset.exactVehicleId) ? false : browseAll };
      const url = catalogStateUrl(window.location.href, location.origin, state);
      if (url.href !== window.location.href) window.history[mode === 'push' ? 'pushState' : 'replaceState'](window.history.state, '', url.href);
    }

    function closeFilters(restoreFocus = true) {
      const version = ++filterVersion;
      cancelFilterMotion();
      openButton?.setAttribute('aria-expanded', 'false');
      const finish = () => {
        if (version !== filterVersion) return;
        cancelFilterMotion();
        if (panel) panel.open = false;
        if (filterBackdrop) filterBackdrop.hidden = true;
        document.body?.classList?.remove('bm-new-filter-open');
        filterSheet?.removeAttribute('role');
        filterSheet?.removeAttribute('aria-modal');
        filterSheet?.removeAttribute('inert');
        if (restoreFocus) openButton?.focus({ preventScroll: true });
      };
      if (!restoreFocus || !panel?.open || desktop.matches || reducedMotion.matches || !filterSheet?.animate) { finish(); return; }
      filterSheet.setAttribute('inert', '');
      filterAnimations = [filterSheet.animate([{ opacity: 1, transform: 'translateY(0)' }, { opacity: .6, transform: 'translateY(26px)' }],
        { duration: 180, easing: 'ease-in', fill: 'both' })];
      if (filterBackdrop?.animate) filterAnimations.push(filterBackdrop.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 160, easing: 'ease-in', fill: 'both' }));
      filterAnimations[0].finished.then(finish).catch(() => {});
    }

    function openFilters() {
      if (!panel || desktop.matches) return;
      filterVersion++; cancelFilterMotion();
      panel.open = true;
      filterSheet?.removeAttribute('inert');
      if (filterBackdrop) filterBackdrop.hidden = false;
      document.body?.classList?.add('bm-new-filter-open');
      filterSheet?.setAttribute('role', 'dialog');
      filterSheet?.setAttribute('aria-modal', 'true');
      openButton?.setAttribute('aria-expanded', 'true');
      if (filterSheet?.animate && !reducedMotion.matches) {
        filterAnimations = [filterSheet.animate([{ opacity: .6, transform: 'translateY(26px)' }, { opacity: 1, transform: 'translateY(0)' }],
          { duration: 210, easing: 'cubic-bezier(.22,.75,.22,1)' })];
        if (filterBackdrop?.animate) filterAnimations.push(filterBackdrop.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 160, easing: 'ease-out' }));
      }
      (filterClose || panel.querySelector('input,button'))?.focus();
    }

    const scrollToCatalog = () => form.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    restoreLocation();

    function filters(includeScope = true) {
      const result = { offset, limit: 24, sort: { 'brand-design': 'BRAND_DESIGN_ASC', 'price-ascending': 'PRICE_ASC', 'price-descending': 'PRICE_DESC' }[form.elements.sort_by.value] };
      if (root.hasAttribute('data-bm-new-search')) result.search = root.dataset.bmNewSearchQuery || '';
      for (const [name, key] of [['diameter', 'diameter'], ['finish', 'finish'], ['manufacturer', 'manufacturer'], ['design', 'designName']]) {
        const selected = selectedValues(name);
        if (selected.length) result[key] = selected;
      }
      for (const [name, key] of [['price_min', 'minPriceCents'], ['price_max', 'maxPriceCents']]) {
        const input = form.elements[name];
        if (input.value !== '' && input.validity.valid) {
          // Customer budget is the displayed SQUARE set total; P12 still filters physical unit cents.
          const setPriceCents = Math.round(Number(input.value) * 100);
          if (Number.isSafeInteger(setPriceCents) && setPriceCents >= 0) {
            result[key] = name === 'price_min' ? Math.ceil(setPriceCents / 4) : Math.floor(setPriceCents / 4);
          }
        }
      }
      return includeScope && collectionScope ? { ...result, ...collectionScope } : result;
    }

    // P12 provides exact Vehicle-bound finishes and their own setOptions.
    // The generation view renders these already validated products separately.
    function generationFinishEntries(families) {
      return families.flatMap(family => family.finishOptions.map(finish => ({ family, finish })));
    }

    function finishSetPrice4(finish) {
      const totals = finish.setOptions
        .filter(option => option.setType === 'SQUARE' && option.defaultQuantity === 4
          && option.unitPrice?.currency === 'EUR' && Number.isSafeInteger(option.unitPrice.amountCents)
          && option.unitPrice.amountCents > 0)
        .map(option => option.unitPrice.amountCents * 4)
        .filter(Number.isSafeInteger);
      return totals.length ? { amountCents: Math.min(...totals), currency: 'EUR' } : null;
    }
    function renderWheelFeatures(host, attrs) {
      if (!host) return;
      host.replaceChildren();
      const add = (kind, label) => {
        const badge = document.createElement('span');
        badge.className = `bm-new-feature bm-new-feature--${kind}`;
        badge.textContent = label;
        host.append(badge);
      };
      if (attrs?.winterSuitable === true) add('winter', 'Wintergeschikt');
      if (attrs?.configurable === true) add('configurable', 'Configureerbaar');
      host.hidden = host.children.length === 0;
    }

    function card(family, index, selectedFinish = null, selectedVehicle = null) {
      const item = template.content.firstElementChild.cloneNode(true);
      const primary = selectedFinish || family.primaryProduct;
      const title = `${displayLabel(family.manufacturer)} ${displayLabel(family.designName)}`;
      const primaryFinish = selectedFinish || family.finishOptions.find(f => f.shopifyProductRef === primary.shopifyProductRef);
      const label = `${title} — ${displayLabel(primaryFinish.finish)}`;
      item.querySelectorAll('.bm-inventory-card__media, .bm-new-card__title a, .bm-new-card__cta').forEach(link => {
        bindResultProductLink(link, primaryFinish, selectedVehicle);
        link.setAttribute('aria-label', `Bekijk velg: ${label}`);
      });
      const image = item.querySelector('.bm-inventory-card__image');
      const imageUrl = window.BandenmeestersNewClient?.shopifyImageUrl;
      // If a CDN resize fails, first retry this exact canonical wheel photo without a
      // transform. Never show the browser's broken-image icon or substitute a fake wheel.
      let retriedOriginal = false;
      image.addEventListener?.('error', () => {
        if (!retriedOriginal && imageUrl && image.currentSrc !== primary.primaryImageUrl) {
          retriedOriginal = true;
          image.removeAttribute('srcset');
          image.src = primary.primaryImageUrl;
          return;
        }
        image.removeAttribute('srcset');
        image.hidden = true;
        const media = item.querySelector('.bm-new-card__media');
        if (media && !media.querySelector('.bm-new-card__media-error')) {
          const message = document.createElement('span');
          message.className = 'bm-new-card__media-error';
          message.textContent = 'Foto niet beschikbaar';
          media.append(message);
        }
      });
      image.loading = index < 4 ? 'eager' : 'lazy';
      image.decoding = 'async';
      image.fetchPriority = index < 2 ? 'high' : 'auto';
      if (imageUrl) {
        image.srcset = [240, 480, 720, 960].map(width => `${imageUrl(primary.primaryImageUrl, width)} ${width}w`).join(', ');
        image.sizes = root.dataset.density === 'single'
          ? '(min-width:781px) 56vw, 100vw'
          : '(min-width:1101px) 300px, 50vw';
      }
      image.src = imageUrl?.(primary.primaryImageUrl, 480) || primary.primaryImageUrl;
      image.alt = label; image.loading = index < 4 ? 'eager' : 'lazy';
item.querySelector('[data-bm-new-maker-text]').textContent = displayLabel(family.manufacturer);
      const officialMark = item.querySelector('[data-bm-new-maker-logo]');
      const officialMaker = officialMakerLogos.get(String(family.manufacturer || ''));
      if (officialMark && officialMaker) {
        officialMark.src = officialMaker.logo;
        officialMark.alt = String(family.manufacturer) + ' logo';
        officialMark.dataset.surface = officialMaker.surface === 'dark' ? 'dark' : 'light';
        officialMark.hidden = false;
        officialMark.addEventListener('error', () => { officialMark.hidden = true; officialMark.removeAttribute('src'); }, { once: true });
      }
      item.querySelector('.bm-new-card__title a').textContent = displayLabel(family.designName);
      item.querySelector('.bm-new-card__finish-label').textContent = displayLabel(primaryFinish.finish);
      const supplierStatus = resultSupplierStatus(primaryFinish, selectedVehicle?.newVehicle?.vehicleId, window.BandenmeestersNewClient);
      if (supplierStatus) {
        const status = document.createElement('p');
        status.className = 'bm-new-card__supplier-status';
        status.dataset.state = supplierStatus.state;
        status.textContent = supplierStatus.label;
        item.querySelector('.bm-new-card__finish-row').after(status);
      }
      const diameters = item.querySelector('.bm-new-card__diameters');
      const visibleSizes = selectedFinish
        ? [...new Set(selectedFinish.setOptions.map(option => Number(option.diameterIn)))].sort((a, b) => a - b)
        : family.sizeOptions;
      visibleSizes.forEach(size => { const li = document.createElement('li'); li.className = 'bm-new-card__diameter'; li.textContent = `${size}″`; diameters.append(li); });
      diameters.hidden = !visibleSizes.length;
      // Reuse this exact card. Place factual sizes after the real finish choices,
      // matching the approved homepage card rhythm without rewriting product data.
      if (root.hasAttribute('data-bm-new-category')) {
        item.querySelector('.bm-new-card__finish-row')?.after?.(diameters);
      }
      const priceText = item.querySelector('.bm-new-card__price strong');
      const priceLine = item.querySelector('.bm-new-card__price > span:first-child');
      const priceUnit = item.querySelector('.bm-new-card__price-unit');
    const commerceFacts = item.querySelector('[data-bm-card-commerce]');
    if (commerceFacts) commerceFacts.textContent = window.BandenmeestersNewClient?.cardCommerceFacts?.(primaryFinish) || 'Voorraad en levering bij maatkeuze';
      // A published design without a confirmed canonical price is still useful to browse.
      // Never manufacture €0, use Shopify's DRAFT price, or show "Vanaf" without an amount.
      const shownPrice = selectedFinish ? finishSetPrice4(selectedFinish) : family.fromSetPrice4;
      if (shownPrice === null) {
        if (priceLine?.firstChild?.nodeType === 3) priceLine.firstChild.textContent = '';
        if (priceText) priceText.textContent = 'Prijs op aanvraag';
        if (priceUnit) priceUnit.hidden = true;
      } else if (priceText) {
        priceText.textContent = money(shownPrice.amountCents);
      }
      const finishes = item.querySelector('.bm-new-card__finishes');
      const finishChoices = selectedFinish ? [] : family.finishOptions;
      if (finishChoices.length > 1) {
        item.querySelector('.bm-new-card__finish-row')?.classList.add('has-siblings');
      }
      finishChoices.slice(0, 3).forEach(finish => {
        const link = finishTemplate.content.firstElementChild.cloneNode(true);
        bindResultProductLink(link, finish, selectedVehicle); link.setAttribute('aria-label', `${title} — ${displayLabel(finish.finish)}`);
        if (finish.shopifyProductRef === primary.shopifyProductRef) { link.classList.add('is-active'); link.setAttribute('aria-current', 'page'); }
        const photo = link.querySelector('img');
        // Product finish choices are above the fold for the first actual family.
        // Load them immediately; only subsequent family cards remain lazy.
        photo.loading = index < 2 ? 'eager' : 'lazy';
        photo.decoding = 'async';
        photo.addEventListener?.('error', () => {
          link.hidden = true;
          if ([...finishes.children].filter((element) => !element.hidden).length < 2) {
            finishes.hidden = true;
          }
        });
        photo.src = imageUrl?.(finish.primaryImageUrl, 160) || finish.primaryImageUrl;
        photo.alt = `${title} ${displayLabel(finish.finish)}`;
        const finishName = link.querySelector('.bm-new-card__finish-name');
        if (finishName) finishName.textContent = displayLabel(finish.finish);
        finishes.append(link);
      });
      finishes.hidden = finishChoices.length < 2;
      renderWheelFeatures(item.querySelector('.bm-new-card__features'),
        selectedFinish ? selectedFinish.sharedTechnicalAttributes : family.sharedTechnicalAttributes);
      const wishlist = item.querySelector('[data-bm-new-wishlist]');
      wishlist.dataset.productId = primary.shopifyProductRef; wishlist.dataset.productUrl = primary.productUrl;
      wishlist.setAttribute('aria-label', `Bewaar ${label} in je verlanglijst`);
      return item;
    }

    function searchFacet(name) {
      const input = root.querySelector('[data-bm-new-facet-search="' + name + '"]');
      const container = root.querySelector('[data-bm-new-facet="' + name + '"]');
      const term = String(input?.value || '').trim().toLocaleLowerCase('nl-BE');
      container?.querySelectorAll('.bm-stock-check').forEach(label => {
        const selected = label.querySelector('input')?.checked;
        label.hidden = !selected && Boolean(term) && !label.textContent.toLocaleLowerCase('nl-BE').includes(term);
      });
    }

    function renderFacets(facets) {
      const focused = document.activeElement;
      const restoreFocus = focused?.matches?.('input[type="checkbox"]') && root.contains(focused)
        ? { name: focused.name, value: focused.value } : null;
      for (const name of ['diameter', 'finish', 'manufacturer', 'design']) {
        const container = root.querySelector(`[data-bm-new-facet="${name}"]`);
        if (scopedCollection && name === 'manufacturer') {
          container.replaceChildren();
          const group = container.closest?.('.bm-stock-facet');
          if (group) group.hidden = true;
          continue;
        }
        const selected = selectedValues(name);
        const offered = facets[name].map(String);
        // Keep an already selected control removable when its result set is empty.
        const controls = [...offered, ...selected.filter(value => !offered.includes(value))];
        container.replaceChildren(...controls.map(value => {
          const label = document.createElement('label'); label.className = 'bm-stock-check';
          const input = document.createElement('input'); input.type = 'checkbox'; input.name = name; input.value = value; input.checked = selected.includes(value);
          const text = document.createElement('span'); text.textContent = `${name === 'diameter' ? value : displayLabel(value)}${name === 'diameter' ? '″' : ''}`;
          label.append(input, text); return label;
        }));
        const group = container.closest?.('.bm-stock-facet');
        if (group) {
          group.hidden = controls.length === 0;
          if (selected.length && group.tagName === 'DETAILS') group.open = true;
        }
        searchFacet(name);
      }
      if (brandEntryPending) {
        brandEntryPending = false;
        const brandGroup = root.querySelector('[data-bm-new-facet-group="manufacturer"]');
        if (brandGroup) brandGroup.open = true;
        if (!desktop.matches) openFilters();
      }
      pendingFacets = null;
      if (restoreFocus) [...form.querySelectorAll('input[type="checkbox"]')]
        .find(input => input.name === restoreFocus.name && input.value === restoreFocus.value)?.focus();
    }

    function renderChips() {
      chips.replaceChildren();
      const active = [...form.querySelectorAll('input[type="checkbox"]:checked'), ...['price_min', 'price_max'].map(name => form.elements[name]).filter(input => input.value !== '' && input.validity.valid)];
      active.forEach(input => {
        const button = document.createElement('button'); button.type = 'button'; button.className = 'bm-new-results__filter-chip';
        const label = document.createElement('span'); label.textContent = input.type === 'checkbox' ? input.closest('label').textContent : `${input.name === 'price_min' ? 'Vanaf' : 'Tot'} ${formatSetBudget(input.value)} per set (4 velgen)`;
        const close = document.createElement('span'); close.className = 'bm-new-results__filter-chip-close'; close.setAttribute('aria-hidden', 'true');
        button.setAttribute('aria-label', `Verwijder filter ${label.textContent}`); button.append(label, close);
        button.addEventListener('click', () => { if (input.type === 'checkbox') input.checked = false; else input.value = ''; schedule(true, 0, 'push'); });
        chips.append(button);
      });
      chips.hidden = !active.length;
      const label = openButton?.querySelector('span'); if (label) label.textContent = active.length ? `Filters (${active.length})` : 'Filters';
    }

    function renderPagination(page) {
      pagination.replaceChildren();
      const totalPages = Math.ceil(page.total / page.limit), current = Math.floor(page.offset / page.limit) + 1;
      pagination.hidden = totalPages < 2;
      const add = (number, label) => {
        const link = document.createElement('a'); link.href = `#${root.id}`; link.textContent = label;
        link.setAttribute('aria-label', `Pagina ${number}`);
        link.addEventListener('click', event => { event.preventDefault(); offset = (number - 1) * page.limit; schedule(false, 0, 'push'); }); pagination.append(link);
      };
      if (current > 1) add(current - 1, '←');
      for (let number = Math.max(1, current - 2); number <= Math.min(totalPages, current + 2); number++) {
        if (number === current) { const span = document.createElement('span'); span.className = 'is-current'; span.textContent = String(number); span.setAttribute('aria-current', 'page'); pagination.append(span); }
        else add(number, String(number));
      }
      if (current < totalPages) add(current + 1, '→');
    }

    async function load(expected) {
      const vehicleOwner = window.BandenmeestersVehicle;
      const vehicle = root.dataset.exactVehicleId ? {vehicleId:root.dataset.exactVehicleId} : vehicleOwner?.getNewVehicle?.();
      const context = root.querySelector('[data-bm-new-vehicle-context]');
      const hasVehicle = Boolean(vehicle?.vehicleId);
      if (root.hasAttribute('data-bm-new-search')) { root.hidden = !hasVehicle; if (!hasVehicle) return; }
      const browsingAll = !root.dataset.exactVehicleId && browseAll;
      const activeVehicle = vehicleOwner?.getActive?.() || null;
      const vehicleLabel = root.dataset.exactVehicleLabel || (hasVehicle ? vehicleOwner.vehicleLabel(activeVehicle) : '');
      const runtimeVehicleIdentity = hasVehicle && !browsingAll;
      root.dataset.vehicleIdentity = String(runtimeVehicleIdentity);
      if (heading) {
        if (runtimeVehicleIdentity && !root.dataset.exactVehicleId) {
          const brand = activeVehicle?.newVehicle?.brandLabel || activeVehicle?.brand?.name || '';
          const model = activeVehicle?.newVehicle?.modelLabel || activeVehicle?.model?.name || '';
          heading.textContent = `Velgen voor ${[brand, model].filter(Boolean).join(' ')}`.trim();
          const from = activeVehicle?.newVehicle?.yearFrom;
          const to = activeVehicle?.newVehicle?.yearTo;
          const years = from ? `${from}–${to || 'heden'}` : '';
          const exact = activeVehicle?.newVehicle?.label || '';
          if (vehicleMeta) {
            vehicleMeta.textContent = [years, exact].filter(Boolean).join(' · ');
            vehicleMeta.hidden = !vehicleMeta.textContent;
          }
          const logo = activeVehicle?.brand?.logo || '';
          if (vehicleLogo) {
            if (logo) { vehicleLogo.src = logo; vehicleLogo.hidden = false; }
            else { vehicleLogo.removeAttribute('src'); vehicleLogo.hidden = true; }
          }
        } else {
          heading.textContent = 'Nieuwe velgen';
          if (vehicleMeta) { vehicleMeta.textContent = ''; vehicleMeta.hidden = true; }
          if (vehicleLogo) { vehicleLogo.removeAttribute('src'); vehicleLogo.hidden = true; }
        }
      }
      root.dataset.hasVehicle = String(hasVehicle);
      root.dataset.browseMode = hasVehicle && !browsingAll ? 'vehicle' : 'all';
      if (context) {
        context.hidden = !hasVehicle;
        if (!context.hidden) context.querySelector('[data-bm-new-vehicle-label]').textContent = vehicleLabel;
      }
      if (heroVehicleAction) {
        heroVehicleAction.textContent = hasVehicle ? 'Wijzig uitvoering' : 'Kies je auto';
        heroVehicleAction.hidden = Boolean(hasVehicle && browsingAll);
      }
      if (catalogBrowseAction) catalogBrowseAction.hidden = !hasVehicle || browsingAll;
      if (browseNote) {
        browseNote.hidden = !browsingAll;
        if (browseNoteCopy && browsingAll) browseNoteCopy.textContent = scopedCollection ? 'Je bekijkt de nieuwe velgen van dit merk, zonder voertuigfilter.' : 'Je bekijkt alle nieuwe velgen, zonder voertuigfilter.';
      }
      renderChips();
      // The published assortment is native Shopify data. Vehicle fitment, quotes and
      // optional canonical filters stay Bridge-owned; browsing all does not depend on them.
      const nativeFilters = filters(false);
      const nativeHasFilters = Object.keys(nativeFilters).some(key => !['offset', 'limit', 'sort'].includes(key));
      if (nativeDiscovery?.dataset?.bmNewNativeDiscovery === '' && !scopedCollection && !runtimeVehicleIdentity && !nativeHasFilters) {
        nativeDiscovery.hidden = false;
        const duplicateLinks = root.querySelector('.bm-new-results__seo-discovery');
        if (duplicateLinks) duplicateLinks.hidden = true;
        form.hidden = true;
        grid.replaceChildren(); grid.hidden = true; empty.hidden = true; error.hidden = true;
        pagination.hidden = true; loading.hidden = true;
        count.textContent = '';
        root.dataset.canFilter = 'true'; root.dataset.canSort = 'true';
        root.dataset.state = 'informational';
        region.setAttribute('aria-busy', 'false');
        region.style?.removeProperty('--bm-new-pending-height');
        return;
      }
      form.hidden = false;
      let autoSearchPending = false;
      try {
        const client = window.BandenmeestersNewClient;
        if (!client?.familyListing) throw new Error('Client unavailable');
        const requestVehicleId = browsingAll ? '' : (vehicle?.vehicleId || '');
        if (scopedCollection && !collectionScope) {
          // Liquid binds the immutable Bridge-owned collection source key.
          // Neither the first Shopify collection product nor a title/handle supplies identity.
          const manufacturer = root.dataset.bmNewCollectionManufacturer || '';
          if (root.dataset.bmNewCollectionScope !== 'manufacturer' || !manufacturer ||
              manufacturer.length > 120 || manufacturer.trim() !== manufacturer || /[\u0000-\u001f<>]/.test(manufacturer)) {
            throw Object.assign(new Error('missing_collection_source_binding'), { code: 'missing_collection_binding' });
          }
          collectionScope = { manufacturer };
        }
        const page = await client.familyListing({ vehicleId: requestVehicleId, filters: filters(), signal: abort.signal, priceContract: 'OPTIONAL_CANONICAL_PRICE_V1' });
        transientRetryRemaining = 1;
        if (expected !== revision || !root.isConnected) return;
        const appliedFilters = filters(false);
        const hasUserFilters = Object.keys(appliedFilters).some((key) => !['offset', 'limit', 'sort'].includes(key));
        if (nativeDiscovery) nativeDiscovery.hidden = !nativeDiscoveryVisible({
          scoped: scopedCollection, native: true, families: page.results.length, filtered: hasUserFilters, vehicle: runtimeVehicleIdentity,
        });
        root.dataset.canSort = String(page.total > 1);
        const facetNames = ['diameter', 'finish', 'manufacturer', 'design'];
        const hasAvailableFacets = facetNames.some((name) =>
          !(scopedCollection && name === 'manufacturer') && Array.isArray(page.facets?.[name]) && page.facets[name].length > 0
        );
        const singleCatalogFamily = false; // Customer requested ordinary overview cards, never a PDP-like singleton.
        root.dataset.density = singleCatalogFamily ? 'single' : 'standard';
        root.dataset.canFilter = String(hasUserFilters || page.total > 0 || hasAvailableFacets);
        const distinctFinishes = Boolean(root.dataset.exactVehicleId);
        const displayEntries = distinctFinishes
          ? generationFinishEntries(page.results)
          : page.results.map(family => ({ family, finish: null }));
        const photoOwner = root.dataset.exactVehicleId ? document.querySelector?.('.bm-exact-vehicle[data-vehicle-id]') : null;
        const exactPhoto = photoOwner?.dataset.vehicleId === requestVehicleId
          ? photoOwner.querySelector('[data-bm-canonical-vehicle-image]')?.dataset.bmCanonicalVehicleImage || '' : '';
        const selectedVehicle = !requestVehicleId ? null : root.dataset.exactVehicleId ? {
          source: 'bridge-vehicle', generation: null, providerIds: {},
          brand: { name: root.dataset.exactVehicleBrand || '' }, model: { name: root.dataset.exactVehicleModel || '' },
          newVehicle: { vehicleId: requestVehicleId, label: root.dataset.exactVehicleType || vehicleLabel,
            brandLabel: root.dataset.exactVehicleBrand || '', modelLabel: root.dataset.exactVehicleModel || '',
            yearFrom: root.dataset.exactVehicleYearFrom, yearTo: root.dataset.exactVehicleYearTo,
            vehiclePageUrl: new URL(window.location.href).pathname, image: exactPhoto },
        } : activeVehicle;
        grid.replaceChildren(...displayEntries.map(({ family, finish }, index) => card(family, index, finish, selectedVehicle)));
        lastRenderedVehicle = root.dataset.exactVehicleId || vehicle?.vehicleId || '';
        lastRenderedBrowseAll = browseAll;
        {
          const applied = filters(false);
          const hasUserFilters = Object.keys(applied).some((key) => !['offset', 'limit', 'sort'].includes(key));
          const unfilteredVehicle = hasVehicle && !browsingAll && !hasUserFilters;
          const unfilteredCatalog = !scopedCollection && (!hasVehicle || browsingAll) && !hasUserFilters;
          const unfilteredManufacturer = scopedCollection && !runtimeVehicleIdentity && !hasUserFilters;
          const eyebrow = empty.querySelector('[data-bm-new-empty-eyebrow]');
          const title = empty.querySelector('[data-bm-new-empty-title]') || empty.querySelector('strong');
          const copy = empty.querySelector('[data-bm-new-empty-copy]') || empty.querySelector('span');
          const change = empty.querySelector('[data-bm-new-empty-change]');
          const browse = empty.querySelector('[data-bm-new-browse-all]');
          const clear = empty.querySelector('[data-bm-new-empty-clear]');
          root.dataset.emptyKind = unfilteredManufacturer ? 'brand' : (unfilteredVehicle ? 'vehicle' : (unfilteredCatalog ? 'catalog' : 'filters'));
          if (eyebrow) {
            eyebrow.textContent = unfilteredManufacturer ? 'Voor dit merk' : (unfilteredVehicle ? 'Voor deze uitvoering' : (unfilteredCatalog ? '' : 'Geen resultaten'));
            eyebrow.hidden = unfilteredCatalog;
          }
          if (title) title.textContent = unfilteredManufacturer
            ? 'Nog geen nieuwe velgen van dit merk beschikbaar'
            : (unfilteredVehicle ? 'Geen passende nieuwe velgen gevonden'
              : (unfilteredCatalog ? 'Nog geen nieuwe velgen beschikbaar' : 'Geen nieuwe velgen gevonden met deze filters.'));
          if (copy) copy.textContent = unfilteredManufacturer
            ? 'Bekijk alle nieuwe velgen of kies je auto voor andere designs.'
            : (unfilteredVehicle ? 'Bekijk alle velgen ter inspiratie of kies een andere auto.'
              : (unfilteredCatalog ? 'Er zijn momenteel geen nieuwe velgen beschikbaar in deze catalogus.' : 'Pas je filters aan om de beschikbare designs te bekijken.'));
          if (change) { change.hidden = !unfilteredVehicle; change.textContent = 'Wijzig uitvoering'; }
          if (browse) browse.hidden = !unfilteredVehicle;
          if (clear) clear.hidden = unfilteredVehicle || unfilteredCatalog || unfilteredManufacturer;
        }
        if (page.results.length) delete root.dataset.emptyKind;
        grid.hidden = !page.results.length; empty.hidden = Boolean(page.results.length); error.hidden = true;
        count.textContent = !page.results.length && root.dataset.emptyKind === 'brand'
          ? ''
          : distinctFinishes
            ? displayEntries.length + ' ' + (displayEntries.length === 1 ? 'resultaat' : 'resultaten')
              + (page.total > page.results.length ? ' op deze pagina' : '')
            : page.total + ' ' + (page.total === 1 ? 'velgdesign' : 'velgdesigns');
        let autoStart = false;
        if (requestVehicleId) {
          const coverage = page.fitmentCoverage;
          const fitmentStatus = coverage?.status || (page.results.length ? 'KNOWN_MATCHES' : 'NOT_RESEARCHED');
          root.dataset.fitmentStatus = fitmentStatus;
          // This page continues the customer's just-completed vehicle selection.
          // Direct page views, restored old choices and filter interactions stay GET-only.
          const selectedAt = activeVehicle?.selectedAt;
          const recentChoice = activeVehicle?.newVehicle?.vehicleId === requestVehicleId
            && Number.isFinite(selectedAt) && Date.now() >= selectedAt && Date.now() - selectedAt < 45_000;
          autoStart = fitmentStatus === 'NOT_RESEARCHED' && !page.results.length && !hasUserFilters
            && recentChoice && autoSearchVehicleId !== requestVehicleId && typeof client.searchVehicleFitment === 'function';
          if (autoStart) autoSearchVehicleId = requestVehicleId;
          // Unknown coverage cannot certify an empty vehicle assortment.
          // The customer must be able to start the first explicit search here.
          if (!page.results.length && (!hasUserFilters || coverage?.coverage === 'PARTIAL')) {
            {
              const title = empty.querySelector('[data-bm-new-empty-title]');
              const copy = empty.querySelector('[data-bm-new-empty-copy]');
              const unknown = fitmentStatus === 'NOT_RESEARCHED';
              const searchedWithoutConfirmedMatches = unknown && Boolean(coverage?.searchRef);
              const pending = fitmentStatus === 'PENDING';
              const unavailable = fitmentStatus === 'TEMPORARILY_UNAVAILABLE';
              const incompleteCheck = unavailable || pending || unknown;
              // A successful supplier source read with no exact matching BM
              // products is not an API outage and is not a certified NO for
              // every other wheel. Present an honest assortment result.
              const noConfirmedProductMatches = unavailable && coverage?.reason === 'PRODUCT_MATCHING_INCOMPLETE';
              const eyebrow = empty.querySelector('[data-bm-new-empty-eyebrow]');
              if (eyebrow && incompleteCheck) {
                eyebrow.textContent = pending ? 'Controle loopt' : noConfirmedProductMatches ? 'Geen bevestigde matches' : 'Controle niet voltooid';
                eyebrow.hidden = false;
              }
              if (searchVehicleButton) {
                // Once a durable search exists, clicking again cannot manufacture
                // missing exact product identity or authorize another provider GET.
                searchVehicleButton.hidden = autoStart || coverage?.reason === 'PRODUCT_MATCHING_INCOMPLETE' || ((!unknown || searchedWithoutConfirmedMatches) && !unavailable);
                searchVehicleButton.disabled = false;
                searchVehicleButton.textContent = unavailable ? 'Opnieuw passende velgen zoeken' : 'Zoek passende velgen voor mijn auto';
              }
              if (title && unknown) title.textContent = searchedWithoutConfirmedMatches
                ? 'Controle momenteel niet mogelijk; probeer opnieuw'
                : 'Zoek velgen voor jouw auto';
              if (copy && unknown) copy.textContent = searchedWithoutConfirmedMatches
                ? 'De bron- of productcontrole is niet volledig. Vraag ons de controle te herstellen of bekijk velgen ter inspiratie.'
                : 'Klik op Zoek passende velgen om het aanbod voor jouw auto te bekijken.';
              if (copy && fitmentStatus === 'PROVEN_NO_MATCHES') copy.textContent = 'Er staan momenteel geen passende nieuwe velgen in ons assortiment voor deze uitvoering.';
              if (title && pending) title.textContent = 'Velgen voor jouw auto zoeken…';
              if (copy && pending) copy.textContent = 'We zoeken passende velgen voor jouw uitvoering.';
              if (title && unavailable) title.textContent = noConfirmedProductMatches ? 'Geen bevestigde passende velgen gevonden' : 'Zoeken lukt momenteel niet';
              if (copy && unavailable) copy.textContent = noConfirmedProductMatches ? 'Voor deze uitvoering hebben we momenteel geen velgen in ons aanbod met bevestigde passing. Dit betekent niet dat andere velgen ongeschikt zijn.' : 'Probeer opnieuw of bekijk alle nieuwe velgen.';
              if (fitmentStatus === 'KNOWN_MATCHES') {
                if (title) title.textContent = hasUserFilters ? 'Geen bevestigde matches met deze filters' : 'Passing van ons aanbod nog niet volledig bevestigd';
                if (copy) copy.textContent = hasUserFilters ? 'Pas de filters aan om andere bevestigde matches te bekijken.' : 'Bekijk het overige aanbod ter inspiratie of vraag persoonlijk velgenadvies.';
              }
              // Exact vehicle pages already show the customer's auto and have
              // the native Shopify enquiry form directly below these results.
              if (root.dataset.exactVehicleId && !hasUserFilters
                && (fitmentStatus === 'PROVEN_NO_MATCHES' || fitmentStatus === 'KNOWN_MATCHES'
                  || coverage?.reason === 'PRODUCT_MATCHING_INCOMPLETE')) {
                if (eyebrow) eyebrow.hidden = true;
                if (title) title.textContent = 'Momenteel geen passende velgen beschikbaar';
                if (copy) copy.textContent = 'Er staan momenteel geen passende velgen met bevestigde passing voor jouw auto online. Laat hieronder je e-mailadres achter, dan zoeken we persoonlijk voor je.';
              }
              empty.hidden = false;
            }
            count.textContent = '';
          } else if (searchVehicleButton) searchVehicleButton.hidden = true;
          if (fitmentStatus === 'PENDING' && client.watchVehicleFitment) {
            client.watchVehicleFitment({ vehicleId: requestVehicleId, signal: abort.signal,
              onUpdate: next => {
                if (expected !== revision || !root.isConnected) return;
                root.dispatchEvent?.(new CustomEvent('bm-new:fitment-status', { bubbles: true,
                  detail: { vehicleId: requestVehicleId, status: next.status, coverage: next } }));
                if (next.status !== 'PENDING' || next.matchedSkus > coverage.matchedSkus) schedule(false, 0, 'none');
              },
              onError: () => {
                if (expected !== revision || !root.isConnected) return;
                root.dataset.fitmentStatus = 'TEMPORARILY_UNAVAILABLE';
                if (browseNoteCopy) browseNoteCopy.textContent = 'De passingcontrole is tijdelijk niet bereikbaar. Vernieuw de resultaten om opnieuw te controleren.';
                        },
            });
          }
        } else delete root.dataset.fitmentStatus;
        renderFacets(page.facets); renderChips(); renderPagination(page); window.BMNewWishlist?.refresh?.();
        root.dataset.state = page.results.length ? 'ready' : 'empty';
        if (nativeDiscovery && !nativeDiscovery.hidden) {
          empty.hidden = true; count.textContent = ''; root.dataset.state = 'informational';
          root.dataset.canFilter = 'false'; root.dataset.canSort = 'false';
        }
        if (requestVehicleId) root.dispatchEvent?.(new CustomEvent('bm-new:fitment-status', { bubbles: true,
          detail: { vehicleId: requestVehicleId, status: root.dataset.fitmentStatus, coverage: page.fitmentCoverage || null } }));
        if (requestVehicleId && autoStart) {
          // The selector fires a keepalive POST before navigation. The confirmed
          // arrival retries that same deduplicated intent if its GET won the race.
          // The existing worker/outbox owns the provider call, never the page GET.
          autoSearchPending = true;
          root.dataset.state = 'loading'; empty.hidden = true;
          void client.searchVehicleFitment({ vehicleId: requestVehicleId }).then(() => {
            if (expected === revision && root.isConnected) schedule(false, 0, 'none');
          }).catch(() => {
            if (expected !== revision || !root.isConnected) return;
            autoSearchPending = false; loading.hidden = true; region.setAttribute('aria-busy', 'false');
            root.dataset.state = 'empty'; root.dataset.fitmentStatus = 'TEMPORARILY_UNAVAILABLE';
            const title = empty.querySelector('[data-bm-new-empty-title]');
            const copy = empty.querySelector('[data-bm-new-empty-copy]');
            if (title) title.textContent = 'Zoeken lukt momenteel niet';
            if (copy) copy.textContent = 'Probeer opnieuw of bekijk alle nieuwe velgen.';
            if (searchVehicleButton) searchVehicleButton.hidden = false;
            empty.hidden = false;
          });
        }
      } catch (failure) {
        if (expected !== revision || abort.signal.aborted) return;
        const temporaryFailure = failure?.code === 'network_error' ||
          [429, 500, 502, 503, 504].includes(failure?.status) || failure?.message === 'Client unavailable';
        if (temporaryFailure && transientRetryRemaining > 0) {
          transientRetryRemaining--;
          schedule(false, 600, 'none');
          return;
        }
        if (failure.code === 'missing_collection_binding') {
          if (nativeDiscovery) nativeDiscovery.hidden = true;
          grid.replaceChildren(); grid.hidden = true; empty.hidden = false; error.hidden = true; pagination.hidden = true;
          const title = empty.querySelector('[data-bm-new-empty-title]');
          const copy = empty.querySelector('[data-bm-new-empty-copy]');
          if (title) title.textContent = 'Er zijn momenteel geen nieuwe velgen van dit merk beschikbaar.';
          if (copy) copy.textContent = 'Bekijk alle nieuwe velgen of zoek op jouw auto.';
          const change = empty.querySelector('[data-bm-new-empty-change]');
          if (change) { change.hidden = false; change.textContent = hasVehicle ? 'Wijzig auto' : 'Kies je auto'; }
          const clear = empty.querySelector('[data-bm-new-empty-clear]');
          const browse = empty.querySelector('[data-bm-new-browse-all]');
          if (clear) clear.hidden = true;
          if (browse) browse.hidden = true;
          root.dataset.canFilter = 'false'; root.dataset.canSort = 'false';
          count.textContent = ''; root.dataset.state = 'collection_unavailable';
          return;
        }
        grid.replaceChildren(); grid.hidden = true; empty.hidden = true; error.hidden = false; pagination.hidden = true;
        if (nativeDiscovery) {
          const filtered = Object.keys(filters(false)).some(key => !['offset', 'limit', 'sort'].includes(key));
          nativeDiscovery.hidden = !nativeDiscoveryVisible({ scoped: scopedCollection, native: true, filtered, vehicle: runtimeVehicleIdentity });
          if (!nativeDiscovery.hidden) {
            const copy = error.querySelector('span');
            if (copy) copy.textContent = 'De maat- en prijsgegevens konden niet worden geladen.';
            root.dataset.canFilter = 'false'; root.dataset.canSort = 'false';
          }
        }
        count.textContent = ''; root.dataset.state = 'error';
      } finally {
        if (expected === revision) {
          grid.inert = false;
          pagination.inert = false;
          loading.hidden = autoSearchPending ? false : true;
          region.setAttribute('aria-busy', autoSearchPending ? 'true' : 'false');
          region.style?.removeProperty('--bm-new-pending-height');
        }
      }
    }

    let lastScheduledVehicle = null;
    let lastScheduledBrowseAll = null;
    let lastRenderedVehicle = null;
    let lastRenderedBrowseAll = null;
    function schedule(reset = true, delay = 0, historyMode = 'replace', passive = false) {
      const currentVehicle = root.dataset.exactVehicleId || window.BandenmeestersVehicle?.getNewVehicle?.()?.vehicleId || '';
      // Ready/initial replay cannot abort an identical request or blank its grid.
      if (passive && lastScheduledVehicle === currentVehicle && lastScheduledBrowseAll === browseAll) return;
      lastScheduledVehicle = currentVehicle;
      lastScheduledBrowseAll = browseAll;
      if (reset) offset = 0;
      writeLocation(historyMode);
      const currentHeight = region.getBoundingClientRect?.().height;
      if (currentHeight > 0) region.style?.setProperty('--bm-new-pending-height', `${currentHeight}px`);
      clearTimeout(timer); abort?.abort(); abort = new AbortController(); const expected = ++revision;
      // Keep verified cards visible for sort/filter updates in the same vehicle context.
      // A different vehicle or browse mode must never display stale passing claims.
      const keepRows = root.dataset.state === 'ready' && !grid.hidden && grid.children.length > 0
        && lastRenderedVehicle === currentVehicle && lastRenderedBrowseAll === browseAll;
      grid.inert = keepRows;
      pagination.inert = keepRows;
      grid.hidden = !keepRows; empty.hidden = true; error.hidden = true;
      if (!keepRows) pagination.hidden = true;
      loading.hidden = keepRows;
      if (nativeDiscovery) {
        const filtered = Object.keys(filters(false)).some(key => !['offset', 'limit', 'sort'].includes(key));
        nativeDiscovery.hidden = !nativeDiscoveryVisible({ scoped: scopedCollection, native: true, filtered,
          vehicle: Boolean(root.dataset.exactVehicleId || (!browseAll && window.BandenmeestersVehicle?.getNewVehicle?.()?.vehicleId)) });
      }
      root.dataset.density = 'standard';
      root.dataset.state = keepRows ? 'ready' : 'loading'; region.setAttribute('aria-busy', 'true');
      timer = setTimeout(() => load(expected), delay);
    }
    form.addEventListener('submit', event => { event.preventDefault(); schedule(true, 0, 'push'); if (!desktop.matches) closeFilters(); });
    form.addEventListener('change', () => schedule(true, 0, 'push'));
    form.addEventListener('input', event => {
      if (event.target.hasAttribute('data-bm-new-facet-search')) searchFacet(event.target.getAttribute('data-bm-new-facet-search'));
      else if (event.target.type === 'number') schedule(true, 250);
    });
    form.addEventListener('reset', () => queueMicrotask(() => { pendingFacets = null; schedule(true, 0, 'push'); }));
    root.querySelector('[data-bm-new-empty-clear]')?.addEventListener('click', () => form.reset());
    root.querySelectorAll('[data-bm-new-browse-all]').forEach((control) => {
      control.addEventListener('click', (event) => {
        if (Boolean(root.dataset.exactVehicleId)) {
          // Exact vehicle pages always retain their own UUID. Leave that page
          // for the existing explicit inspiration route instead of a dead button.
          event.preventDefault();
          window.location.assign('/collections/nieuwe-velgen?browse=all');
          return;
        }
        event.preventDefault();
        browseAll = true;
        schedule(true, 0, 'push');
        scrollToCatalog();
      });
    });
    let searchInFlight = false;
    const startVehicleSearch = async () => {
      if (searchInFlight) return;
      browseAll = false;
      const vehicleId = root.dataset.exactVehicleId || window.BandenmeestersVehicle?.getNewVehicle?.()?.vehicleId;
      const before = revision;
      if (!vehicleId || !window.BandenmeestersNewClient?.searchVehicleFitment) return;
      searchInFlight = true;
      if (searchVehicleButton) {
        searchVehicleButton.disabled = true;
        searchVehicleButton.textContent = 'Passende velgen zoeken…';
      }
      try {
        // Only an actual customer click can create this governed provider intent.
        await window.BandenmeestersNewClient.searchVehicleFitment({ vehicleId });
        if (before !== revision || !root.isConnected) return;
        schedule(true, 0, 'push'); scrollToCatalog();
      } catch (failure) {
        if (before !== revision || !root.isConnected) return;
        root.dataset.fitmentStatus = 'TEMPORARILY_UNAVAILABLE';
        const title = empty.querySelector('[data-bm-new-empty-title]');
        const copy = empty.querySelector('[data-bm-new-empty-copy]');
        if (title) title.textContent = 'Zoeken lukt momenteel niet';
        if (copy) copy.textContent = 'Probeer opnieuw of bekijk alle nieuwe velgen.';
        empty.hidden = false;
        root.dispatchEvent?.(new CustomEvent('bm-new:fitment-status', { bubbles: true,
          detail: { vehicleId, status: 'TEMPORARILY_UNAVAILABLE', coverage: null } }));
      } finally {
        searchInFlight = false;
        if (searchVehicleButton) {
          searchVehicleButton.disabled = false;
          searchVehicleButton.textContent = 'Opnieuw passende velgen zoeken';
        }
      }
    };
    returnVehicle?.addEventListener('click', startVehicleSearch);
    searchVehicleButton?.addEventListener('click', startVehicleSearch);
    root.querySelector('[data-bm-new-retry]')?.addEventListener('click', () => schedule(false));
    openButton?.addEventListener('click', openFilters);
    filterClose?.addEventListener('click', () => closeFilters());
    filterBackdrop?.addEventListener('click', () => closeFilters());
    panel?.addEventListener('keydown', event => {
      if (desktop.matches || !panel.open) return;
      if (event.key === 'Escape') { event.preventDefault(); closeFilters(); return; }
      if (event.key !== 'Tab' || !filterSheet) return;
      const focusable = [...filterSheet.querySelectorAll('button,input,select,a[href]')].filter(node => !node.disabled && node.offsetParent !== null);
      const first = focusable[0], last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    });
    const vehicleChanged = event => {
      if (event.detail?.evidenceRefresh) {
        const currentVehicle = root.dataset.exactVehicleId || window.BandenmeestersVehicle?.getNewVehicle?.()?.vehicleId || '';
        // URL/label readback for the same UUID does not change P4 matches.
        if (currentVehicle !== lastScheduledVehicle) schedule(false);
      } else {
        // A newly selected car scopes every NEW discovery route. Explicit
        // inspiration mode stays in history until the shopper changes cars.
        browseAll = false;
        schedule();
      }
    };
    const vehicleReady = () => schedule(false, 0, 'replace', true);
    const restoreHistory = () => { if (!desktop.matches) closeFilters(false); restoreLocation(); schedule(false, 0, 'none'); };
    const pageShown = event => { if (event.persisted) restoreHistory(); };
    window.addEventListener('bm:vehicle:change', vehicleChanged); document.addEventListener('bm:vehicle:ready', vehicleReady);
    window.addEventListener('popstate', restoreHistory); window.addEventListener('pageshow', pageShown);
    root.addEventListener('bm-new-results:dispose', () => {
      ++revision; clearTimeout(timer); abort?.abort(); closeFilters(false);
      desktop.removeEventListener('change', syncPanel); window.removeEventListener('bm:vehicle:change', vehicleChanged);
      document.removeEventListener('bm:vehicle:ready', vehicleReady); window.removeEventListener('popstate', restoreHistory);
      window.removeEventListener('pageshow', pageShown);
    }, { once: true });
    schedule(false, 0, 'replace', true);
  }

  function scan(scope) {
    scope.querySelectorAll('[data-bm-new-results]').forEach(root => root.hasAttribute('data-design-mode-fixture') ? initializeFixture(root) : initializeLive(root));
  }
  scan(document);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-bm-new-results]').forEach((root) => root.dispatchEvent(new Event('bm-new-results:dispose')));
  });
})();